import type { Money } from "@/lib/api/types";

/**
 * Render a Money envelope. The server already formatted it for the location's locale
 * (ADR-0015 D6), so the client renders `display` verbatim and performs NO arithmetic.
 * Doing math on `minor` here would violate the server-authoritative-money invariant.
 */
export function money(m: Money | undefined | null): string {
  return m?.display ?? "";
}

/** True when a Money value is zero (safe read of the integer minor unit — not arithmetic). */
export function isZero(m: Money | undefined | null): boolean {
  return !m || m.minor === 0;
}

/**
 * Currency-safe minor-unit conversion and formatting (G-18/G-19, mirrors
 * `applications/coupon-loyalty-management/legacy/webapp/lib/money.ts` — copied, not imported, since apps
 * in this repo do not share code across their boundary).
 *
 * <h3>Why this exists alongside `money()`/`isZero()`</h3>
 *
 * `money()` above is for the common counter case: a `Money` envelope the server already rendered,
 * where the client's only job is to print `display` verbatim. But a handful of call sites hold a
 * cashier-typed rupee amount, a cash-drawer change calculation, or a manually-built minor-unit
 * figure with no server `display` to lean on — and several of those hard-coded `/ 100` or `* 100`,
 * which is correct for INR/AED/GBP today and silently wrong for a KWD (3 decimals) or JPY (0
 * decimals) tenant. Everything below reads the exponent from an explicit ISO 4217 table first, and
 * falls back to `Intl.NumberFormat` for anything else — never assume 2.
 */

/**
 * Explicit ISO 4217 minor-unit exponents for the currencies this platform's launch markets and
 * common non-2-decimal edge cases use. `Intl.NumberFormat` is the fallback for everything else,
 * not the source of truth for these — some environments' CLDR data disagrees with ISO 4217 (for
 * example, both ISK and UGX are 0-decimal per ISO 4217 even though whole-currency conventions
 * vary).
 */
const ISO_4217_EXPONENTS: Readonly<Record<string, number>> = Object.freeze({
  INR: 2,
  AED: 2,
  USD: 2,
  GBP: 2,
  EUR: 2,
  SAR: 2,
  KWD: 3,
  BHD: 3,
  OMR: 3,
  JOD: 3,
  TND: 3,
  JPY: 0,
  KRW: 0,
  CLP: 0,
  VND: 0,
  ISK: 0,
  UGX: 0,
});

/**
 * The number of minor-unit decimal digits for a currency (2 for INR/USD/AED/GBP/EUR, 3 for
 * KWD/BHD/OMR/JOD/TND, 0 for JPY/KRW/CLP/VND/ISK/UGX). Never assume 2 or hard-code `/ 100` —
 * always call this.
 *
 * @throws Error if `code` is not a well-formed currency code (falls through to
 *   `Intl.NumberFormat`, which throws a `RangeError` for a malformed code)
 */
export function currencyExponent(code: string): number {
  if (!code || typeof code !== "string") {
    throw new Error(`Invalid currency code: ${String(code)}`);
  }
  const normalized = code.trim().toUpperCase();
  const known = ISO_4217_EXPONENTS[normalized];
  if (known !== undefined) return known;
  // Fallback for any currency not in the explicit table above. This also validates the code:
  // Intl.NumberFormat throws a RangeError for a malformed currency argument.
  const resolved = new Intl.NumberFormat("en", {
    style: "currency",
    currency: normalized,
  }).resolvedOptions().maximumFractionDigits;
  return resolved ?? 2;
}

/** Exact `10^exponent` scale factor for a currency, e.g. 100 for INR, 1000 for KWD, 1 for JPY. */
function scaleFor(currency: string): number {
  return 10 ** currencyExponent(currency);
}

/** Converts an exact integer (or otherwise already-scaled) minor-unit amount to major units. */
export function minorToMajor(minor: number, currency: string): number {
  return minor / scaleFor(currency);
}

/**
 * Converts a major-unit decimal amount to minor units, rounding HALF_UP (ties away from zero,
 * matching {@code MinorUnits.toMinor}'s `RoundingMode.HALF_UP` on the Java side) at the
 * currency's own exponent.
 *
 * Shifts the decimal point through a string (`${major}e${exponent}`) rather than multiplying
 * (`major * 10 ** exponent`) directly: `major.toString()` is JS's shortest round-trippable
 * decimal for the number, so appending an `e`-exponent and re-parsing lands on the intended
 * decimal value exactly, whereas a binary multiplication can drift a `x.xx5` tie the wrong way
 * (`1.005 * 100 === 100.49999999999999`, which `Math.round` then rounds *down*).
 */
export function majorToMinor(major: number, currency: string): number {
  const exponent = currencyExponent(currency);
  const shifted = Number(`${major}e${exponent}`);
  return major < 0 ? -Math.round(-shifted) : Math.round(shifted);
}

export interface FormatMinorOptions {
  /** BCP 47 locale; defaults to the console locale (`en-IN`). */
  locale?: string;
  /**
   * When true, a whole-number amount (no fractional minor units) formats with zero fraction
   * digits instead of the currency's full exponent — e.g. INR 10000 minor -> "₹100" rather than
   * "₹100.00". Preserves the trailing-zero-dropping behaviour some existing call sites had before
   * they went through this shared helper. Defaults to false (always show the currency's exponent).
   */
  dropTrailingZerosWhenWhole?: boolean;
}

/**
 * Formats an exact integer minor-unit amount as currency text, using the currency's own exponent
 * rather than assuming 2 decimal places.
 *
 * Returns an empty string for a missing/non-finite amount, matching this app's convention that an
 * absent figure is not the same fact as a zero one.
 */
export function formatMinor(
  minor: number | null | undefined,
  currency: string,
  locale: string = "en-IN",
  options?: FormatMinorOptions
): string {
  if (typeof minor !== "number" || !Number.isFinite(minor)) return "";
  const exponent = currencyExponent(currency);
  const major = minorToMajor(minor, currency);
  const dropZeros = options?.dropTrailingZerosWhenWhole ?? false;
  const fractionDigits = dropZeros && Number.isInteger(major) ? 0 : exponent;
  return new Intl.NumberFormat(options?.locale ?? locale, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(major);
}

/** Returns the locale's short currency marker for labels beside cashier-entered amounts. */
export function currencySymbol(currency: string, locale: string = "en-IN"): string {
  return (
    new Intl.NumberFormat(locale, { style: "currency", currency: currency.toUpperCase() })
      .formatToParts(0)
      .find((part) => part.type === "currency")?.value ?? currency.toUpperCase()
  );
}

/**
 * Cash amounts in major currency units that a cashier is likely to receive as a note.
 *
 * These are deliberately denomination presets, not converted INR values: a £5 note and a ₹100
 * note are useful in their own tills regardless of an exchange rate. Unknown currencies use a
 * conservative generic ladder and still get filtered against the bill before being shown.
 */
const CASH_PRESETS_BY_CURRENCY: Readonly<Record<string, readonly number[]>> = Object.freeze({
  INR: [100, 200, 500, 2000],
  GBP: [5, 10, 20, 50],
  USD: [5, 10, 20, 50],
  EUR: [5, 10, 20, 50],
  AED: [10, 20, 50, 100],
  KWD: [1, 5, 10, 20],
  JPY: [500, 1000, 2000, 5000],
});

const GENERIC_CASH_PRESETS = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000] as const;

/**
 * Returns two to four practical cash shortcuts that cover the remaining bill.
 *
 * The caller passes the remaining amount in minor units, while the returned values are major units
 * because they are also used as the cashier's typed cash amount. For unusually large bills beyond
 * the configured notes, an amount rounded to a sensible power-of-ten cash step is added so the
 * control never disappears or offers only an amount below what is owed.
 */
export function cashPresetAmounts(remainingMinor: number, currency: string): number[] {
  if (!Number.isFinite(remainingMinor) || remainingMinor <= 0) return [];

  const normalized = currency.trim().toUpperCase();
  const configured = CASH_PRESETS_BY_CURRENCY[normalized] ?? GENERIC_CASH_PRESETS;
  const dueMajor = minorToMajor(remainingMinor, normalized);
  const covering = configured.filter((amount) => majorToMinor(amount, normalized) >= remainingMinor).slice(0, 4);
  if (covering.length >= 2) return [...covering];

  const orderOfMagnitude = 10 ** Math.max(0, Math.floor(Math.log10(Math.max(dueMajor, 1))));
  const cashStep = Math.max(10 ** -currencyExponent(normalized), orderOfMagnitude / 10);
  const roundedDue = Math.ceil(dueMajor / cashStep) * cashStep;
  const extra = [roundedDue, roundedDue * 2, roundedDue * 5].filter((amount) => amount >= dueMajor);
  return [...new Set([...covering, ...extra])].sort((left, right) => left - right).slice(0, 4);
}
